import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getEkScoreCommercialTerms, saveEkScoreCommercialTerms } from '../ecosystemPartners.service';
import { resultMessage } from '../../../../service/apiResult';
import { detailPageCardSx as pageCardSx, detailSectionTitleSx, detailValueSx } from '../../../styles/detailStyles';

const BILLING_PERIODS = [
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'ONE_TIME', label: 'One time' },
];

const headCellSx = { fontSize: 13, fontWeight: 600, color: '#625B71', py: 1.25, whiteSpace: 'nowrap' };

const emptyTier = () => ({ name: '', maxFarmers: '', price: '', billingPeriod: 'YEARLY', active: true });

const toForm = (terms) => ({
  farmerSharePercent: terms?.farmerSharePercent ?? '',
  currency: terms?.currency || 'INR',
  tiers: (terms?.tiers || []).map((t) => ({
    name: t.name,
    maxFarmers: String(t.maxFarmers),
    price: String(t.price),
    billingPeriod: t.billingPeriod,
    active: t.active,
  })),
});

const toNumberOrNull = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

/** Expected earning per farmer for one tier: price x share% / max farmers (null when not computable). */
const earningPerFarmer = (tier, share) => {
  const price = toNumberOrNull(tier.price);
  const max = toNumberOrNull(tier.maxFarmers);
  const pct = toNumberOrNull(share);
  if (price === null || pct === null || !max || max <= 0) return null;
  return (price * pct) / 100 / max;
};

/**
 * eK Score commercial terms of the integrator's ecosystem, set by eKutir: the subscription tiers an
 * eK Score Customer pays for and the share of it that goes to the farmers whose scores were shared.
 * Nothing is pre-filled: an ecosystem starts with no tiers and no share.
 */
const EkScoreCommercialTerms = ({ ecosystemId }) => {
  const [form, setForm] = useState(toForm(null));
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    if (!ecosystemId) return;
    setLoading(true);
    const result = await getEkScoreCommercialTerms(ecosystemId);
    setLoading(false);
    if (result.success) {
      setForm(toForm(result.data));
      setConfigured(Boolean(result.data?.configured));
    } else {
      setMessage({ severity: 'error', text: resultMessage(result, 'Could not load the eK Score commercial terms.') });
    }
  }, [ecosystemId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateTier = (index, patch) =>
    setForm((prev) => ({ ...prev, tiers: prev.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t)) }));

  const removeTier = (index) =>
    setForm((prev) => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== index) }));

  const addTier = () => setForm((prev) => ({ ...prev, tiers: [...prev.tiers, emptyTier()] }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const payload = {
      farmerSharePercent: toNumberOrNull(form.farmerSharePercent),
      currency: (form.currency || '').trim().toUpperCase() || null,
      tiers: form.tiers.map((t, index) => ({
        name: t.name.trim(),
        maxFarmers: toNumberOrNull(t.maxFarmers),
        price: toNumberOrNull(t.price),
        billingPeriod: t.billingPeriod,
        active: t.active,
        sortOrder: index,
      })),
    };
    const result = await saveEkScoreCommercialTerms(ecosystemId, payload);
    setSaving(false);
    if (result.success) {
      setForm(toForm(result.data));
      setConfigured(true);
      setMessage({ severity: 'success', text: 'eK Score commercial terms saved.' });
    } else {
      setMessage({ severity: 'error', text: resultMessage(result, 'Could not save the eK Score commercial terms.') });
    }
  };

  const formatMoney = (value) =>
    value === null ? '-' : `${form.currency || ''} ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <Box sx={{ ...pageCardSx, mt: 3 }}>
      <Typography sx={detailSectionTitleSx}>eK Score commercial terms</Typography>
      <Typography sx={{ ...detailValueSx, mb: 2, color: '#625B71' }}>
        What eK Score Customers of this ecosystem pay, and the share of it that goes to the farmers whose scores were shared.
        {!configured && !loading ? ' Not configured yet.' : ''}
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={2.25} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Farmer share (%)"
            type="number"
            fullWidth
            size="small"
            value={form.farmerSharePercent}
            onChange={(e) => setForm((prev) => ({ ...prev, farmerSharePercent: e.target.value }))}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            helperText="0 to 100, up to 2 decimals. Leave empty while not decided."
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Currency"
            fullWidth
            size="small"
            value={form.currency}
            onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))}
            inputProps={{ maxLength: 3 }}
            helperText="3-letter ISO code, e.g. INR"
          />
        </Grid>
      </Grid>

      <TableContainer sx={{ border: '1px solid #DFE5F6', borderRadius: '16px', mb: 2 }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: '#EEF1FA' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F4F6FC' }}>
              {['Tier name', 'Max farmers', 'Price', 'Billing period', 'Active', 'Per farmer', ''].map((head) => (
                <TableCell key={head} sx={headCellSx}>{head}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {form.tiers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography sx={detailValueSx}>No subscription tiers yet.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              form.tiers.map((tier, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={index}>
                  <TableCell>
                    <TextField size="small" value={tier.name} placeholder="e.g. Starter"
                      onChange={(e) => updateTier(index, { name: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={tier.maxFarmers} inputProps={{ min: 1 }}
                      onChange={(e) => updateTier(index, { maxFarmers: e.target.value })} sx={{ width: 120 }} />
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={tier.price} inputProps={{ min: 0, step: 0.01 }}
                      onChange={(e) => updateTier(index, { price: e.target.value })} sx={{ width: 140 }} />
                  </TableCell>
                  <TableCell>
                    <TextField select size="small" value={tier.billingPeriod} sx={{ width: 130 }}
                      onChange={(e) => updateTier(index, { billingPeriod: e.target.value })}>
                      {BILLING_PERIODS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Checkbox checked={Boolean(tier.active)}
                      onChange={(e) => updateTier(index, { active: e.target.checked })} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(earningPerFarmer(tier, form.farmerSharePercent))}
                  </TableCell>
                  <TableCell>
                    <IconButton aria-label="Remove tier" onClick={() => removeTier(index)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={addTier} disabled={loading || saving}>Add tier</Button>
        <Button variant="contained" onClick={save} disabled={loading || saving || !ecosystemId}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default EkScoreCommercialTerms;
